import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  listSubmissions,
  getProgressStages,
  setSubmissionProgress,
  AdminSubmissionRow,
  ProgressStage
} from "../../api/admin";
import { formConfig } from "../../formConfig";

const STORAGE_KEY = "adminSelectedColumns_v2";
const DEFAULT_COLUMNS = ["projectName", "jobNumber", "__submittedBy", "__submittedAt", "__progress"];

function loadColumns(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return DEFAULT_COLUMNS;
}

function formatValue(v: unknown): string {
  if (v == null || v === "") return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    if (typeof o.displayName === "string") return o.displayName;
    if (typeof o.name === "string" && typeof o.url === "string") return o.name;
    return JSON.stringify(o);
  }
  return String(v);
}

function autoShort(question: string): string {
  const cleaned = question
    .replace(/[?:.,]+$/, "")
    .replace(/^(please\s+|what\s+(is|are)\s+the\s+|how\s+|which\s+|who\s+(is|are)\s+the\s+)/i, "")
    .replace(/\s+will\s+be\s+managing\s+this\s+project/i, "")
    .replace(/\s+for\s+each\s+building\/structure/i, "")
    .replace(/\s+\(.*?\)\s*/g, " ")
    .trim();
  const words = cleaned.split(/\s+/);
  const short = words.slice(0, 4).join(" ");
  return short.charAt(0).toUpperCase() + short.slice(1);
}

export default function AdminList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AdminSubmissionRow[]>([]);
  const [stages, setStages] = useState<ProgressStage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const selectedCols = loadColumns();

  useEffect(() => {
    listSubmissions().then(setRows).catch((e) => setError(e.message ?? "Failed to load"));
    getProgressStages().then(setStages);
  }, []);

  const fieldsById = useMemo(() => {
    const m = new Map<string, { id: string; question: string; shortLabel?: string; section: string }>();
    formConfig.forEach((sec) => {
      sec.fields.forEach((f) => m.set(f.id, { id: f.id, question: f.question, shortLabel: f.shortLabel, section: sec.shortTitle }));
    });
    return m;
  }, []);

  const builtIns = useMemo(() => {
    const list: { id: string; label: string }[] = [
      { id: "__submittedBy", label: "Submitted by" },
      { id: "__submittedAt", label: "Submitted" },
      { id: "__progress", label: "Progress" }
    ];
    stages.forEach((s) => list.push({ id: `__stage:${s.id}`, label: s.label }));
    return list;
  }, [stages]);
  const builtInsById = useMemo(() => new Map(builtIns.map((b) => [b.id, b])), [builtIns]);

  async function toggleProgress(rowId: string, partition: string, stageId: string, current: boolean) {
    try {
      const updated = await setSubmissionProgress(rowId, partition, stageId, !current);
      setRows((rs) => rs.map((r) => (r.id === rowId ? { ...r, progress: updated.progress } : r)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update");
    }
  }

  return (
    <>
      <div className="admin-page-head">
        <h1>Submissions</h1>
        <span className="hint">{rows.length} total</span>
      </div>
      {error && <div className="banner-error">{error}</div>}
      <div className="admin-table-wrap">
        <table className="admin-table compact">
          <thead>
            <tr>
              {selectedCols.map((id) => {
                const f = fieldsById.get(id);
                const b = builtInsById.get(id);
                const stageMatch = id.startsWith("__stage:") ? stages.find((s) => `__stage:${s.id}` === id) : null;
                const label = f?.shortLabel || (f ? autoShort(f.question) : null) || b?.label || stageMatch?.label || id;
                const fullTitle = f?.question ?? b?.label ?? stageMatch?.label ?? id;
                const cls = id === "__progress" || id.startsWith("__stage:") ? "progress-col" : "";
                return <th key={id} title={fullTitle} className={cls}>{label}</th>;
              })}
              <th className="actions-col"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const goView = () => navigate(`/admin/submissions/${r.id}?submissionDate=${r.submissionDate}`);
              return (
                <tr key={r.id} className="row-clickable" onClick={goView}>
                  {selectedCols.map((id) => {
                    if (id === "__submittedBy") return <td key={id}>{r.submittedBy?.userDetails}</td>;
                    if (id === "__submittedAt") return <td key={id}>{new Date(r.submittedAt).toLocaleDateString()}</td>;
                    if (id === "__progress") {
                      return (
                        <td key={id} className="progress-cell-group" onClick={(e) => e.stopPropagation()}>
                          <div className="progress-pills">
                            {stages.map((s) => {
                              const entry = r.progress?.[s.id];
                              const done = !!entry?.done;
                              return (
                                <button
                                  key={s.id}
                                  type="button"
                                  className={"progress-pill " + (done ? "done" : "")}
                                  onClick={() => toggleProgress(r.id, r.submissionDate, s.id, done)}
                                  title={`${s.label}${done ? ` · ${entry?.updatedBy?.userDetails ?? ""}` : ""}`}
                                >
                                  {done ? "✓" : "○"}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      );
                    }
                    if (id.startsWith("__stage:")) {
                      const stageId = id.slice("__stage:".length);
                      const entry = r.progress?.[stageId];
                      const done = !!entry?.done;
                      return (
                        <td key={id} className="progress-cell" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className={"progress-pill " + (done ? "done" : "")}
                            onClick={() => toggleProgress(r.id, r.submissionDate, stageId, done)}
                            title={done ? `Done · ${entry?.updatedBy?.userDetails ?? ""}` : "Mark done"}
                          >
                            {done ? "✓" : "○"}
                          </button>
                        </td>
                      );
                    }
                    return <td key={id}>{formatValue(r.data?.[id])}</td>;
                  })}
                  <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                    <Link className="row-action" to={`/admin/submissions/${r.id}?submissionDate=${r.submissionDate}`}>View</Link>
                    {" · "}
                    <Link className="row-action" to={`/admin/submissions/${r.id}?submissionDate=${r.submissionDate}&edit=1`}>Edit</Link>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && !error && (
              <tr><td colSpan={selectedCols.length + 1} className="hint">No submissions yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
